import {
  PaymentMethodsLabels,
  PaymentMethodsValues,
} from "@/enums/expenseFields";
import { ExpenseDetailProps } from "@/types/expense";
import {
  DialogHeader,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";
import { Badge } from "@/uiComponents/components/ui/badge";
import { Check, X } from "lucide-react";
import React from "react";

const ExpenseDetail: React.FC<ExpenseDetailProps> = ({
  onClose,
  isOpen,
  expense,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-800">
            Expense Details
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 py-4 max-h-[70vh] overflow-y-auto px-2">
          {expense && (
            <div className="space-y-6">
              {/* Header Section */}
              <div className="grid  grid-cols-[70%_30%]">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">
                    {expense.description || "No description"}
                  </h3>
                  <p className="text-lg text-gray-500 mt-1">
                    {new Date(expense.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div className="bg-gray-100 px-3 py-1 rounded-full">
                  <span className="text-lg font-medium text-gray-800">
                    NPR {expense.amount}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200"></div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <DetailItem
                    label="Category"
                    value={expense.categoryName}
                    icon="🗂️"
                  />
                  <DetailItem
                    label="Branch"
                    value={expense.branchName}
                    icon="🏢"
                  />
                  <DetailItem
                    label="Payment Method"
                    value={
                      expense.paymentMethod === PaymentMethodsValues.QR
                        ? PaymentMethodsLabels.ONLINE
                        : expense.paymentMethod === PaymentMethodsValues.CHEQUE
                          ? PaymentMethodsLabels.CHEQUE
                          : expense.paymentMethod.charAt(0).toUpperCase() +
                            expense.paymentMethod.slice(1)
                    }
                    icon="💳"
                  />
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <DetailItem
                    label="Reimbursed To"
                    value={expense.reimbursedTo}
                    icon="👤"
                  />
                  <DetailItem
                    label="Reimbursed"
                    value={
                      <span
                        className={`inline-flex items-center ${expense.reimbursed ? "text-green-600" : "text-gray-600"}`}
                      >
                        {expense.reimbursed ? (
                          <>
                            <Check className="h-4 w-4 mr-1" /> Yes
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-1" /> No
                          </>
                        )}
                      </span>
                    }
                    icon="🔄"
                  />
                  <DetailItem
                    label="Items"
                    value={
                      expense.items.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {expense.items.map((item) => (
                            <Badge
                              //@ts-expect-error:skip
                              key={item.id}
                              variant="outline"
                              className="text-sm font-normal"
                            >
                              {
                                //@ts-expect-error:skip
                                item.name
                              }
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        "No items"
                      )
                    }
                    icon="🏷️"
                  />
                </div>
              </div>

              {/* Bill Photos Section */}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Reusable detail item component
const DetailItem = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: string;
}) => (
  <div className="h-[7vh]">
    <div className="flex items-center text-sm font-medium text-gray-500 mb-1">
      {icon && <span className="mr-2">{icon}</span>}
      {label}
    </div>
    <div className="text-lg text-gray-800">{value}</div>
  </div>
);

export default ExpenseDetail;
